// packages/chatbot/src/hooks/controller/useHeadlessToolRuntime.ts

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ActiveToolCall, HeadlessToolRegistry, UIMessage } from '../../types';

type Params = {
  messages: UIMessage[];
  headlessTools?: HeadlessToolRegistry | Record<string, (args: any, ctx?: any) => Promise<any> | any>;
  headlessContext?: any;
  submitToolResult: (toolCallId: string, result: any) => void;

  // When true, the transcript is being hydrated from history (or otherwise loading);
  // never auto-run headless tools during hydration.
  isHistoryLoading?: boolean;

  // Used to reset per-conversation execution set
  conversationId?: string | null;
};

type Result = {
  activeToolCall: ActiveToolCall | null;
  onSubmitToolResponse: (toolCallId: string, result: any) => void;
  onCancelTool: () => void;
  onReplayFrontendActions: (toolCallId: string) => void;
};

export function useHeadlessToolRuntime({
  messages,
  headlessTools,
  headlessContext,
  submitToolResult,
  isHistoryLoading = false,
  conversationId,
}: Params): Result {
  const [activeToolCall, setActiveToolCall] = useState<ActiveToolCall | null>(null);
  const executedToolCallIdsRef = useRef<Set<string>>(new Set());

  // Reset executed tool-call set and pending interactive tool call on conversation change
  useEffect(() => {
    executedToolCallIdsRef.current = new Set();
    setActiveToolCall(null);
  }, [conversationId]);

  useEffect(() => {
    if (!messages || messages.length === 0) return;

    // Never auto-run anything while hydrating/loading history
    if (isHistoryLoading) return;

    const last = messages[messages.length - 1] as any;
    if (!last || last.type !== 'tool_call_start') return;

    const toolName: string = last.tool_name;
    const args: any = last.arguments;
    const toolCallId: string = last.tool_call_id;
    const meta = last.meta;

    // Only auto-run for live stream-originated tool calls.
    // Persisted/history tool calls never include meta.live === true.
    const isLive = Boolean(meta && meta.live === true);
    if (!isLive) return;

    if (executedToolCallIdsRef.current.has(toolCallId)) return;

    const registry = headlessTools || {};
    const headless = (registry as any)[toolName];

    if (typeof headless === 'function') {
      (async () => {
        try {
          const result = await headless(args, headlessContext);
          submitToolResult(toolCallId, result ?? { status: 'success' });
        } catch (e: any) {
          submitToolResult(toolCallId, {
            status: 'error',
            message: e?.message || 'Headless tool failed.',
          });
        } finally {
          executedToolCallIdsRef.current.add(toolCallId);
        }
      })();
    } else {
      // Interactive tool call: show UI
      setActiveToolCall({ callId: toolCallId, toolName, args });
      executedToolCallIdsRef.current.add(toolCallId);
    }
  }, [messages, headlessTools, headlessContext, submitToolResult, isHistoryLoading]);

  const onSubmitToolResponse = useCallback(
    (toolCallId: string, result: any) => {
      setActiveToolCall(null);
      submitToolResult(toolCallId, result);
    },
    [submitToolResult]
  );

  const onCancelTool = useCallback(() => {
    setActiveToolCall(null);
  }, []);

  const onReplayFrontendActions = useCallback(
    async (toolCallId: string) => {
      try {
        const call = messages.find(
          (m) => (m as any).type === 'tool_call_start' && (m as any).tool_call_id === toolCallId
        ) as any;
        if (!call) return;

        const args = call.arguments;
        const fn = (headlessTools as any)?.frontend_actions;

        if (typeof fn === 'function') {
          const result = await fn(args, headlessContext);
          submitToolResult(toolCallId, result ?? { status: 'success' });
        }
      } catch {
        // ignore
      }
    },
    [messages, headlessTools, headlessContext, submitToolResult]
  );

  return {
    activeToolCall,
    onSubmitToolResponse,
    onCancelTool,
    onReplayFrontendActions,
  };
}
